<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { mockResources, mockAgency } from '../mock/gameState'

const route = useRoute()

const navItems = [
  { path: '/game/dashboard', label: '事务所', icon: '🏢' },
  { path: '/game/idols', label: '偶像', icon: '🌟' },
  { path: '/game/shows', label: '演出', icon: '🎪' },
  { path: '/game/settings', label: '设置', icon: '⚙️' },
]

function formatMoney(n: number): string {
  if (n >= 1000000) return '¥' + (n / 10000).toFixed(0) + '万'
  if (n >= 10000) return '¥' + (n / 10000).toFixed(1) + '万'
  return '¥' + n.toLocaleString()
}

function formatFans(n: number): string {
  if (n >= 10000) return (n / 10000).toFixed(1) + 'w'
  return n.toLocaleString()
}

// AP dots array
const apDots = computed(() => {
  return Array.from({ length: mockResources.maxAp }, (_, i) => i < mockResources.ap)
})
</script>

<template>
  <div class="min-h-screen bg-bg-deep flex flex-col">
    <!-- 顶部资源 HUD -->
    <header class="relative bg-bg-base border-b border-accent-purple border-opacity-20 px-4 py-0">
      <!-- 顶部霓虹线 -->
      <div class="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60" />

      <div class="max-w-6xl mx-auto flex items-center justify-between h-14">
        <!-- 左：事务所信息 -->
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-lg bg-bg-surface border border-primary border-opacity-30 flex items-center justify-center shadow-glow-pink">
            <span class="text-sm">🌙</span>
          </div>
          <div>
            <h1 class="text-text-primary font-display font-bold text-sm leading-none">{{ mockAgency.name }}</h1>
            <p class="text-text-muted text-xs mt-0.5">Lv.{{ mockAgency.level }} 事务所</p>
          </div>
          <div class="hidden md:block w-px h-6 bg-accent-purple opacity-20 mx-1" />
          <div class="hidden md:block text-text-secondary text-xs">
            第 <span class="text-text-primary font-medium">{{ mockAgency.date.year }}</span> 年
            <span class="text-text-primary font-medium">{{ mockAgency.date.month }}</span> 月
            Week <span class="text-text-primary font-medium">{{ mockAgency.date.week }}</span>
          </div>
        </div>

        <!-- 右：资源栏 -->
        <div class="flex items-center gap-1 md:gap-3">
          <!-- 资金 -->
          <div class="hud-resource">
            <span class="hud-resource-icon text-accent-gold">¥</span>
            <span class="hud-resource-value">{{ formatMoney(mockResources.money) }}</span>
          </div>

          <div class="hud-divider" />

          <!-- 粉丝 -->
          <div class="hud-resource">
            <span class="hud-resource-icon">👥</span>
            <span class="hud-resource-value">{{ formatFans(mockResources.totalFans) }}</span>
          </div>

          <div class="hud-divider" />

          <!-- 人气 -->
          <div class="hud-resource">
            <span class="hud-resource-icon">⭐</span>
            <span class="hud-resource-value text-accent-cyan">{{ mockResources.fame }}</span>
          </div>

          <div class="hud-divider" />

          <!-- AP 行动点 -->
          <div class="flex items-center gap-1.5 bg-bg-surface px-3 py-1.5 rounded-lg border border-accent-purple border-opacity-20">
            <span class="text-text-secondary text-xs font-bold tracking-wider">AP</span>
            <div class="flex items-center gap-1">
              <span
                v-for="(active, i) in apDots"
                :key="i"
                class="ap-dot"
                :class="active ? 'ap-dot--active' : 'ap-dot--empty'"
              />
            </div>
            <span class="text-text-secondary text-xs tabular-nums">{{ mockResources.ap }}/{{ mockResources.maxAp }}</span>
          </div>
        </div>
      </div>
    </header>

    <!-- 主体 -->
    <div class="flex-1 flex overflow-hidden">
      <!-- 侧边导航 -->
      <nav class="w-14 md:w-52 bg-bg-base border-r border-accent-purple border-opacity-10 py-4 flex flex-col">
        <div class="space-y-1 px-2 flex-1">
          <router-link
            v-for="item in navItems"
            :key="item.path"
            :to="item.path"
            class="nav-item"
            :class="route.path === item.path ? 'nav-item--active' : 'nav-item--inactive'"
          >
            <span class="text-xl leading-none flex-shrink-0">{{ item.icon }}</span>
            <span class="hidden md:inline text-sm font-medium">{{ item.label }}</span>
            <span v-if="route.path === item.path" class="hidden md:block ml-auto w-1 h-1 rounded-full bg-primary" />
          </router-link>
        </div>

        <!-- 底部结算按钮 -->
        <div class="px-2 pt-3 border-t border-accent-purple border-opacity-10 mt-2">
          <button class="w-full flex items-center justify-center md:justify-start gap-2 px-3 py-2.5 rounded-lg bg-primary bg-opacity-10 border border-primary border-opacity-30 text-primary text-sm font-medium hover:bg-opacity-20 transition-all group">
            <span class="text-lg leading-none">⏭</span>
            <span class="hidden md:inline">结束本周</span>
          </button>
        </div>
      </nav>

      <!-- 内容区 -->
      <main class="flex-1 overflow-auto">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<style scoped>
.hud-resource {
  @apply flex items-center gap-1.5 px-2 py-1;
}
.hud-resource-icon {
  @apply text-sm leading-none;
}
.hud-resource-value {
  @apply text-text-primary text-sm font-semibold tabular-nums;
}
.hud-divider {
  @apply w-px h-4 bg-accent-purple opacity-15 hidden md:block;
}

.ap-dot {
  @apply w-2.5 h-2.5 rounded-full transition-all;
}
.ap-dot--active {
  @apply bg-primary shadow-[0_0_6px_rgba(255,107,157,0.8)];
}
.ap-dot--empty {
  @apply bg-bg-elevated border border-accent-purple border-opacity-30;
}

.nav-item {
  @apply flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all;
}
.nav-item--active {
  @apply bg-primary bg-opacity-10 text-primary border border-primary border-opacity-20;
}
.nav-item--inactive {
  @apply text-text-muted hover:text-text-primary hover:bg-bg-surface;
}
</style>
